import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { Role } from '../../../core/enums/role.enum';
import { IconComponent } from '../icon/icon.component';

interface NavLink {
  label: string;
  icon: string;
  route: string;
}

interface NavGroup {
  label: string;
  icon: string;
  children: NavLink[];
}

type NavEntry = NavLink | NavGroup;

const SIDEBAR_CONFIG: Record<string, NavEntry[]> = {
  [Role.SUPER_ADMIN]: [
    { label: 'Dashboard', icon: 'chart-bar', route: '/super-admin' },
    {
      label: 'Catalog',
      icon: 'building',
      children: [
        { label: 'Facilities', icon: 'building', route: '/super-admin/facilities' },
        { label: 'Farms', icon: 'egg', route: '/super-admin/farms' },
        { label: 'Customers', icon: 'user', route: '/super-admin/customers' },
        { label: 'Product Master', icon: 'chart-bar', route: '/super-admin/product-master' },
      ],
    },
    {
      label: 'Orders & Shipments',
      icon: 'package',
      children: [
        { label: 'Orders', icon: 'package', route: '/super-admin/orders' },
        { label: 'Shipment Entries', icon: 'truck', route: '/super-admin/shipment-entries' },
      ],
    },
    {
      label: 'Operations',
      icon: 'clipboard',
      children: [
        { label: 'Operations Tracker', icon: 'clipboard', route: '/super-admin/operations-tracker' },
        { label: 'Inward Accounts', icon: 'arrow-down-tray', route: '/super-admin/inward-accounts' },
        { label: 'Grade In Accounts', icon: 'check-circle', route: '/super-admin/grade-in-accounts' },
        { label: 'Grade Out Accounts', icon: 'arrow-up-tray', route: '/super-admin/grade-out-accounts' },
        { label: 'Packing Accounts', icon: 'package', route: '/super-admin/packing-accounts' },
      ],
    },
    {
      label: 'Administration',
      icon: 'users',
      children: [
        { label: 'All Users', icon: 'users', route: '/super-admin/users' },
        { label: 'Activity Log', icon: 'clipboard', route: '/super-admin/activity-log' },
      ],
    },
  ],
  [Role.FARM]: [
    { label: 'Dashboard', icon: 'chart-bar', route: '/farm' },
    { label: 'Flocks', icon: 'egg', route: '/farm/flocks' },
    { label: 'Shipments', icon: 'truck', route: '/farm/shipments' },
  ],
  [Role.FACILITY]: [
    { label: 'Dashboard', icon: 'chart-bar', route: '/facility' },
    { label: 'Operations Tracker', icon: 'clipboard', route: '/facility/operations-tracker' },
    { label: 'Orders', icon: 'package', route: '/facility/orders' },
    { label: 'Shipment Entries', icon: 'truck', route: '/facility/shipment-entries' },
  ],
  [Role.CUSTOMER]: [
    { label: 'Dashboard', icon: 'chart-bar', route: '/customer' },
    { label: 'Order Management', icon: 'package', route: '/customer/orders' },
  ],
  [Role.INWARD]: [
    { label: 'Dashboard', icon: 'chart-bar', route: '/inward' },
    { label: 'Incoming Shipments', icon: 'arrow-down-tray', route: '/inward/shipments' },
    { label: 'Inward', icon: 'clipboard', route: '/inward/logs' },
  ],
  [Role.GRADE_IN]: [
    { label: 'Dashboard', icon: 'chart-bar', route: '/grade-in' },
    { label: 'Inward Logs', icon: 'clipboard', route: '/grade-in/inward-logs' },
    { label: 'Grade In Logs', icon: 'receipt', route: '/grade-in/logs' },
  ],
  [Role.GRADE_OUT]: [
    { label: 'Dashboard', icon: 'chart-bar', route: '/grade-out' },
    { label: 'Grade In Logs', icon: 'clipboard', route: '/grade-out/grade-in-logs' },
    { label: 'Grade Out Logs', icon: 'arrow-up-tray', route: '/grade-out/logs' },
  ],
  [Role.PACKING]: [
    { label: 'Dashboard', icon: 'chart-bar', route: '/packing' },
    { label: 'Order Management', icon: 'clipboard', route: '/packing/orders' },
    { label: 'Shipment Entries', icon: 'truck', route: '/packing/shipment-entries' },
  ],
};

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, IconComponent],
  template: `
    <!-- Mobile overlay -->
    @if (mobileOpen() && isMobile()) {
      <div
        class="fixed inset-0 top-20 bg-black/40 backdrop-blur-sm z-30 lg:hidden animate-fade-in"
        (click)="mobileOpen.set(false)"
      ></div>
    }

    <aside
      class="fixed top-20 left-0 h-[calc(100vh-5rem)] bg-white z-40
             transition-all duration-300 ease-in-out flex flex-col border-r border-gray-100 shadow-sm"
      [ngClass]="{
        'w-64': (!isMobile() && !collapsed()) || (isMobile() && mobileOpen()),
        'w-20': !isMobile() && collapsed(),
        '-translate-x-full': isMobile() && !mobileOpen(),
        'translate-x-0': !isMobile() || mobileOpen()
      }"
    >
      <!-- Navigation -->
      <nav class="flex-1 overflow-y-auto py-4 px-3 space-y-1 scrollbar-hide">
        @for (entry of navItems; track entry.label) {
          <!-- Single link -->
          @if (asLink(entry); as item) {
            <a
              [routerLink]="item.route"
              routerLinkActive="bg-primary-50 text-primary-800 font-semibold border-primary-500"
              [routerLinkActiveOptions]="{ exact: item.route === getDashboardRoute() }"
              class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600
                     border-l-2 border-transparent
                     hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150 group"
              [class.justify-center]="collapsed() && !isMobile()"
              [attr.title]="collapsed() && !isMobile() ? item.label : null"
              (click)="onNavClick()"
            >
              <span class="transition-transform duration-200 group-hover:scale-110 shrink-0">
                <app-icon [name]="item.icon" [size]="20" />
              </span>
              @if (showLabels()) {
                <span class="animate-fade-in truncate tracking-wide">{{ item.label }}</span>
              }
            </a>
          }

          <!-- Collapsible group -->
          @if (asGroup(entry); as group) {
            <button
              type="button"
              class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 group cursor-pointer"
              [ngClass]="groupHasActive(group)
                ? 'text-primary-800 font-semibold'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'"
              [class.justify-center]="collapsed() && !isMobile()"
              [attr.title]="collapsed() && !isMobile() ? group.label : null"
              (click)="toggleGroup(group.label)"
            >
              <span class="transition-transform duration-200 group-hover:scale-110 shrink-0">
                <app-icon [name]="group.icon" [size]="20" />
              </span>
              @if (showLabels()) {
                <span class="animate-fade-in truncate tracking-wide flex-1 text-left">{{ group.label }}</span>
                <svg
                  class="w-4 h-4 shrink-0 opacity-60 transition-transform duration-200"
                  [class.rotate-90]="isGroupOpen(group)"
                  fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              }
            </button>

            @if (isGroupOpen(group) && showLabels()) {
              <div class="ml-4 pl-3 border-l border-gray-100 space-y-1 animate-fade-in">
                @for (child of group.children; track child.route) {
                  <a
                    [routerLink]="child.route"
                    routerLinkActive="bg-primary-50 text-primary-800 font-semibold"
                    class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-500
                           hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150"
                    (click)="onNavClick()"
                  >
                    <span class="truncate tracking-wide">{{ child.label }}</span>
                  </a>
                }
              </div>
            }
          }
        }
      </nav>

      <!-- Collapse toggle (desktop only) -->
      @if (!isMobile()) {
        <div class="px-3 py-3 border-t border-gray-100 shrink-0">
          <button
            class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-500
                   hover:bg-gray-50 hover:text-gray-800 transition-all duration-200 cursor-pointer"
            [class.justify-center]="collapsed()"
            (click)="collapsed.set(!collapsed())"
          >
            <svg class="w-5 h-5 transition-transform duration-300 opacity-70" [class.rotate-180]="collapsed()" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            @if (!collapsed()) {
              <span class="animate-fade-in tracking-wide">Collapse</span>
            }
          </button>
        </div>
      }
    </aside>
  `,
})
export class SidebarComponent {
  private auth = inject(AuthService);
  private router = inject(Router);
  collapsed = signal(false);
  mobileOpen = signal(false);

  // Track the current URL reactively so the active group stays expanded.
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  // Manually-toggled group (single-open accordion). The active group is always open regardless.
  expandedGroup = signal<string | null>(null);

  isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 1024;
  }

  showLabels(): boolean {
    return !this.collapsed() || (this.isMobile() && this.mobileOpen());
  }

  get navItems(): NavEntry[] {
    const role = this.auth.userRole();
    return role ? (SIDEBAR_CONFIG[role] || []) : [];
  }

  asLink(entry: NavEntry): NavLink | null {
    return 'route' in entry ? entry : null;
  }

  asGroup(entry: NavEntry): NavGroup | null {
    return 'children' in entry ? entry : null;
  }

  groupHasActive(group: NavGroup): boolean {
    const url = this.currentUrl();
    return group.children.some((c) => url === c.route || url.startsWith(c.route + '/'));
  }

  isGroupOpen(group: NavGroup): boolean {
    return this.expandedGroup() === group.label || this.groupHasActive(group);
  }

  toggleGroup(label: string): void {
    // When collapsed on desktop, expand the rail first so children are visible.
    if (this.collapsed() && !this.isMobile()) {
      this.collapsed.set(false);
      this.expandedGroup.set(label);
      return;
    }
    this.expandedGroup.set(this.expandedGroup() === label ? null : label);
  }

  getDashboardRoute(): string {
    const role = this.auth.userRole();
    if (!role) return '/';
    const items = SIDEBAR_CONFIG[role];
    const firstLink = items?.find((i) => 'route' in i) as NavLink | undefined;
    return firstLink?.route ?? '/';
  }

  onNavClick(): void {
    if (this.isMobile()) {
      this.mobileOpen.set(false);
    }
  }

  toggle(): void {
    if (this.isMobile()) {
      this.mobileOpen.set(!this.mobileOpen());
    } else {
      this.collapsed.set(!this.collapsed());
    }
  }
}
